import {
  AvailablePlansTypes,
  defaultAgencyPlan,
  defaultAutomatePlan,
  defaultCorePlan,
  defaultCreatePlan,
  defaultEnterprisePlan,
  defaultFreePlan,
  defaultLitePlan,
  defaultOldAgencyPlan,
  defaultProfessionalPlan,
  PLANS
} from '@constants/plans'
import { useFeatureFlags } from '@contexts/featureFlags'
import { useTranslation } from 'react-i18next'

const {
  PROFESSIONAL_100K_23,
  PROFESSIONAL_200K_23,
  PROFESSIONAL_500K_23,
  PROFESSIONAL_1M_23,
  AGENCY_100K_23,
  AGENCY_200K_23,
  AGENCY_500K_23,
  AGENCY_1M_23
} = PLANS

type FeatureType = {
  name: string
  tooltip?: string
}

export const useGetPlanFeatures = ({
  recordKey
}: {
  recordKey: AvailablePlansTypes
}) => {
  const { t } = useTranslation()

  const hasFreePlanRestrictions = useFeatureFlags('FREE_PLAN_RESTRICTIONS')

  const activeFreePlanFeatures = hasFreePlanRestrictions
    ? [
        { name: t('plans.another.features.10') },
        { name: t('plans.another.features.11') }
      ]
    : []

  const professionalPlansFeatures = [
    ...activeFreePlanFeatures,
    { name: t('plans.another.features.1') }
  ]

  const agencyPlansFeatures = [
    ...activeFreePlanFeatures,
    { name: t('plans.another.features.10') },
    { name: t('plans.another.features.11') },
    { name: t('plans.another.features.1') },
    { name: t('plans.another.features.2.agency') },
    {
      name: t('plans.another.features.3')
    },
    {
      name: t('plans.another.features.4'),
      tooltip: t('plans.another.features.4.tooltip')
    }
  ]

  const featuresForPlans = {
    [defaultFreePlan]: [
      { name: t('plans.another.features.10.free') },
      { name: t('plans.another.features.11.free') }
    ],
    [defaultLitePlan]: [...activeFreePlanFeatures],
    [defaultProfessionalPlan]: professionalPlansFeatures,
    [PROFESSIONAL_100K_23]: professionalPlansFeatures,
    [PROFESSIONAL_200K_23]: professionalPlansFeatures,
    [PROFESSIONAL_500K_23]: professionalPlansFeatures,
    [PROFESSIONAL_1M_23]: professionalPlansFeatures,
    [defaultAgencyPlan]: agencyPlansFeatures,
    [AGENCY_100K_23]: agencyPlansFeatures,
    [AGENCY_200K_23]: agencyPlansFeatures,
    [AGENCY_500K_23]: agencyPlansFeatures,
    [AGENCY_1M_23]: agencyPlansFeatures,
    [defaultEnterprisePlan]: [
      { name: t('plans.another.features.1') },
      {
        name: t('plans.another.features.2.enterprise'),
        tooltip: t('plans.another.features.2.enterprise.tooltip')
      },
      { name: t('plans.another.features.3') },
      {
        name: t('plans.another.features.4'),
        tooltip: t('plans.another.features.4.tooltip')
      },
      { name: t('plans.another.features.5') },
      { name: t('plans.another.features.6') },
      { name: t('plans.another.features.7') },
      {
        name: t('plans.another.features.8'),
        tooltip: t('plans.another.features.8.tooltip')
      },
      {
        name: t('plans.another.features.9'),
        tooltip: t('plans.another.features.9.tooltip')
      }
    ],
    [defaultCorePlan]: [],
    [defaultCreatePlan]: [{ name: t('plans.another.features.fonts') }],
    [defaultAutomatePlan]: [
      { name: t('plans.another.features.fonts') },
      { name: t('plans.another.features.global.js') },
      { name: t('plans.another.features.funnels') }
    ],
    [defaultOldAgencyPlan]: [
      { name: t('plans.another.features.fonts') },
      { name: t('plans.another.features.global.js') },
      { name: t('plans.another.features.funnels') },
      { name: t('plans.another.features.unlimited.subaccounts') }
    ]
  }

  return featuresForPlans[recordKey] as FeatureType[]
}
